import React, { useCallback, useContext, useMemo } from 'react';
import _ from 'lodash';
import htmlParser from 'html-react-parser';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import { useSgTrackPageName } from 'saga-modules';
import { CepConstants } from 'saga-cep-specific-components';
import SgCepSideMenu from 'sg-cep-side-menu';
import SgCoverEndorsements from 'sg-cover-endorsements';
import SgImportantStatementsAndDeclarations from 'sg-important-statements-and-declarations';
import SgDeclarationsDocuments from 'sg-declarations-documents';
import SgSphDeclarationsDocumentsUnoccupancy from 'sg-sph-declarations-documents-unoccupancy';
import SgSphDeclarationsBullets from 'sg-sph-declarations-bullets';
import SgSpmDeclarationsBullets from 'sg-spm-declarations-bullets';
import CommonConstants from 'sg-common-constants';
import SphConstants from 'sg-sph-constants';
import SpmConstants from 'sg-spm-constants';
import SgSptConstants from 'sg-spt-constants';
import SphUtils from 'sg-sph-utils';
import { Button, Link } from '@jutro/components';
import { SG_AA_PREMIUM_PAGE_ACTION } from 'sg-adobe-analytics/AdobeAnalyticsConstants';
import { TranslatorContext } from '@jutro/locale';
import { WizardPage } from 'gw-portals-wizard-react';
import { useHistory } from 'react-router-dom';
import SgRenewalUtil from 'sg-renewal-util';
import SgCepLobSpecificUtil from 'sg-cep-lob-specific-util';
import messages from './SgRenewalDeclarationsPage.messages';
import metadata from './SgRenewalDeclarationsPage.metadata.json5';

function SgRenewalDeclarationsPage(props) {
    const {
        wizardData: modelVM,
        goNext,
    } = props;

    const history = useHistory();
    useSgTrackPageName(CepConstants.PAGE_NAMES.STATEMENTS);

    const translator = useContext(TranslatorContext);
    const { renewalDetails } = modelVM.value;

    let selectedAddons = [];
    if (renewalDetails.availableCoverages) {
        selectedAddons = renewalDetails.availableCoverages.filter((cov) => cov.selected);
    }

    const navigateTo = useCallback((path, state) => {
        history.push(path, state);
    }, [history]);

    const lobBulletPoints = useMemo(() => {
        if (renewalDetails.productCode === (CommonConstants.MOTOR_PRODUCT_CODE || CommonConstants.MOTORHOME_PRODUCT_CODE)) {
            return <SgSpmDeclarationsBullets />;
        }
        if (renewalDetails.productCode === CommonConstants.HOME_PRODUCT_CODE) {
            return <SgSphDeclarationsBullets modelVM={modelVM} />;
        }
        if (renewalDetails.productCode === CommonConstants.TRAVEL_PRODUCT_CODE) {
            return null;
        }
        return <></>;
    }, []);

    const lobDocuments = useMemo(() => {
        if (renewalDetails.productCode === (CommonConstants.MOTOR_PRODUCT_CODE || CommonConstants.MOTORHOME_PRODUCT_CODE)) {
            const hasBreakdownAssistance = selectedAddons.find((elem) => elem.name === SpmConstants.BREAKDOWN_NAME);
            if (hasBreakdownAssistance) {
                return (
                    <li>
                        <Link
                            id="breakdownPolicyBookLink"
                            href={SpmConstants.POLICY_DOCUMENTS_LINKS.CAR_BREAKDOWN_BOOKLET}
                            target="_blank"
                            adobeValue={`${SG_AA_PREMIUM_PAGE_ACTION.LEVEL_1.DECLARATIONS}|${SG_AA_PREMIUM_PAGE_ACTION.LEVEL_2.TEXT}|
                                ${SG_AA_PREMIUM_PAGE_ACTION.LEVEL_3.BREAKDOWN_POLICY_BOOKLET}`}
                        >
                            {translator(messages.BreakdownBookletLink)}
                        </Link>
                    </li>
                );
            }
        }
        return <></>;
    }, []);

    const importantInfoLink = useMemo(() => {
        if (renewalDetails.productCode === (CommonConstants.MOTOR_PRODUCT_CODE || CommonConstants.MOTORHOME_PRODUCT_CODE)) {
            return SpmConstants.IMPORTANT_INFORMATION_LINK;
        }
        if (renewalDetails.productCode === CommonConstants.HOME_PRODUCT_CODE) {
            return SphUtils.getImportantInfoLink(modelVM);
        }
        if (renewalDetails.productCode === CommonConstants.TRAVEL_PRODUCT_CODE) {
            return renewalDetails.coverLevel.includes(SgSptConstants.AGGS_COVER_LEVEL_NAME) ? SgSptConstants.SELECT_POLICY_INFO_LINK
                : SgSptConstants.IMPORTANT_INFORMATION_LINK;
        }
        return '';
    }, [modelVM]);

    const sagaPlusPolicyBookLink = useMemo(() => {
        if (renewalDetails.productCode === (CommonConstants.MOTOR_PRODUCT_CODE || CommonConstants.MOTORHOME_PRODUCT_CODE)) {
            return SpmConstants.POLICY_DOCUMENTS_LINKS.POLICY_BOOKLET_SELECT_PLUS;
        }
        if (renewalDetails.productCode === CommonConstants.HOME_PRODUCT_CODE) {
            return SphConstants.POLICY_BOOKLET_URLS.PLUS_URL;
        }
        return '';
    }, []);

    const goBackToSummary = useCallback(() => {
        navigateTo('/renewal',
            {
                policyNumber: renewalDetails.jobNumber,
                isUpgrading: renewalDetails.isUpgrading
            });
    }, [navigateTo, renewalDetails]);

    const buttonContent = useMemo(() => {
        return (
            <>
                <Button type="button" id="backButton" className="sgButton sgSecondaryButton" onClick={goBackToSummary}>
                    {translator(messages.Back)}
                </Button>
                <Button type="button" id="continueButton" className="sgButton sgTransactionButton" onClick={goNext}>
                    {translator(messages.Continue)}
                </Button>
            </>
        );
    }, [goNext, renewalDetails]);

    const overrideProps = {
        coverEndorsementsSection: {
            quoteEndorsements: modelVM.value.quoteEndorsements
        },
        declarationsDocumentsSection: {
            visible: !SgCepLobSpecificUtil.show003EndorsementDeclarations(modelVM),
            importantInfoLink,
            lobDocuments,
            isBullets: true
        },
        declarationsUnoccupancyDocumentsSection: {
            visible: SgCepLobSpecificUtil.show003EndorsementDeclarations(modelVM),
            importantInfoLink
        },
        importantStatementsAndDeclarations: {
            modelVM,
            showFullStatements: false,
            premium: { amount: renewalDetails.premium.amount, currency: 'gbp' },
            isMonthly: !_.isNil(renewalDetails.installmentsInfo_Ext),
            chargeForCredit: !_.isNil(renewalDetails.installmentsInfo_Ext)
                ? renewalDetails.installmentsInfo_Ext.chargeForCredit : undefined,
            showCancellationFeeInfo: !SgRenewalUtil.isSagaPlus(modelVM.value) && renewalDetails.productCode !== CommonConstants.TRAVEL_PRODUCT_CODE,
            showFixedPriceInfo: SgCepLobSpecificUtil.shouldShowFixedPriceInfoOnRenewal(modelVM),
            lobBulletPoints: lobBulletPoints,
            sagaPlusPolicyBook: sagaPlusPolicyBookLink,
            bulletpointMessageShort: renewalDetails.productCode === CommonConstants.TRAVEL_PRODUCT_CODE,
            feeAmount: _.get(renewalDetails, 'arrangementFee_Ext') || 0,
            isAprZero: !_.isNil(renewalDetails.installmentsInfo_Ext) ? renewalDetails.installmentsInfo_Ext.apr === 0 : false
        },
        renewalPageImportantAccept: {
            content: htmlParser(translator(messages.RenewalPageImportantAccept))
        },
        buttonContainer: {
            content: buttonContent
        }
    };

    const resolvers = {
        resolveComponentMap: {
            SgImportantStatementsAndDeclarations,
            SgCoverEndorsements,
            SgDeclarationsDocuments,
            SgSphDeclarationsDocumentsUnoccupancy,
            SgCepSideMenu
        }
    };

    return (
        <WizardPage showPrevious={false} showNext={false}>
            {renderContentFromMetadata(metadata.pageContent, overrideProps, resolvers)}
        </WizardPage>
    );
}

export default SgRenewalDeclarationsPage;
